import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Info, CheckCircle2, Loader2, Download, Mail, AlertTriangle, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const STORAGE_KEY = 'reto21_form_state';

const SUSTANCIAS = ['Alcohol', 'Cocaína', 'Pasta Base', 'Marihuana', 'Benzodiacepinas / Opioides'];

const SALUD_MENTAL = ['Ninguna diagnosticada', 'Depresión', 'Ansiedad generalizada', 'Trastorno bipolar', 'TDAH', 'Trastorno por estrés postraumático (PTSD)', 'Esquizofrenia / psicosis', 'Trastorno de personalidad', 'Trastorno alimenticio', 'Otras'];

const GATILLOS = ['Estrés laboral / financiero', 'Ansiedad', 'Aburrimiento', 'Presión social / amigos consumidores', 'Celebraciones / fiestas', 'Conflictos familiares o de pareja', 'Tristeza o depresión', 'Soledad', 'Enojo o frustración', 'Trauma / recuerdos dolorosos', 'Dinero / recibí pago', 'Insomnio', 'Lugares asociados al consumo', 'Horarios específicos (después del trabajo, fines de semana)'];

const FORTALEZAS = ['Familia que apoya', 'Pareja o amigos cercanos', 'Trabajo o fuente de ingresos', 'Estudios', 'Deporte / actividad física', 'Terapia individual actual', 'Terapia familiar actual', 'Espiritualidad / fe', 'Grupos de apoyo (AA, NA, otros)', 'Hobbies creativos (música, arte, escritura)', 'Mascotas', 'Contacto con naturaleza'];

const HABITOS = ['Leer 10 minutos', 'Meditación / mindfulness', 'Ejercicio físico', 'Alimentación saludable', 'Tomar agua (2L/día)', 'Gratitud diaria (escribir 3 cosas)', 'Dormir 7-8 horas', 'Reunión de apoyo semanal', 'Llamada con terapeuta', 'Journaling diario', 'Caminata al aire libre', 'Rutina matutina consistente', 'Lectura espiritual', 'Conexión familiar diaria', 'Reducir pantallas'];

const AREAS = ['Familia', 'Salud física', 'Salud mental', 'Finanzas', 'Crecimiento personal', 'Relaciones sociales', 'Trabajo / vocación', 'Espiritualidad'];

const GENEROS = [
  { value: 'hombre', label: 'Hombre' },
  { value: 'mujer', label: 'Mujer' },
  { value: 'no_decir', label: 'Prefiero no decir' },
];

const REGIONES_CL = [
  'Arica y Parinacota', 'Tarapacá', 'Antofagasta', 'Atacama', 'Coquimbo',
  'Valparaíso', 'Metropolitana', "O'Higgins", 'Maule', 'Ñuble', 'Biobío',
  'Araucanía', 'Los Ríos', 'Los Lagos', 'Aysén', 'Magallanes', 'Extranjero / Fuera de Chile',
];

const INTENTOS_PREVIOS = [
  { value: 'nunca', label: 'Es mi primer intento' },
  { value: '1', label: '1 vez antes' },
  { value: '2-5', label: '2 a 5 veces' },
  { value: '6+', label: '6 veces o más' },
];

const TOTAL_STEPS = 9; // 8 questions + final result

function InfoTip({ children }) {
  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>
        <button type="button" className="inline-flex items-center text-emerald-400 hover:text-emerald-300 ml-1.5" aria-label="Información">
          <Info className="w-3.5 h-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs bg-slate-900 border border-slate-700 text-slate-200 text-xs leading-relaxed">
        {children}
      </TooltipContent>
    </Tooltip>
  );
}

function MultiSelect({ options, values, onChange, allowExclusive }) {
  const toggle = (opt) => {
    const isSelected = values.includes(opt);
    if (allowExclusive && opt === allowExclusive) {
      onChange(isSelected ? [] : [opt]);
      return;
    }
    if (isSelected) {
      onChange(values.filter(v => v !== opt));
    } else {
      const filtered = allowExclusive ? values.filter(v => v !== allowExclusive) : values;
      onChange([...filtered, opt]);
    }
  };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {options.map(opt => {
        const active = values.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            className={`text-left px-4 py-3 rounded-lg border transition text-sm ${active ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300' : 'border-slate-700 bg-slate-950 text-slate-300 hover:border-slate-600'}`}
            data-testid={`reto21-opt-${opt.toLowerCase().replace(/\W+/g, '-').slice(0, 30)}`}
          >
            <span className="flex items-center gap-2">
              <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${active ? 'border-emerald-400 bg-emerald-500' : 'border-slate-600'}`}>
                {active && <CheckCircle2 className="w-3 h-3 text-black" />}
              </span>
              <span>{opt}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function Reto21DiasPage() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const [form, setForm] = useState({
    nombre: '', edad: '', genero: '', region: '', email: '',
    sustancia: '', anos_consumo: '', edad_inicio: '', intentos_previos: '',
    salud_mental: [],
    gatillos: [], gatillos_custom: '',
    fortalezas: [],
    para_que: '',
    habitos: [], habitos_custom: '',
    areas: [],
    acepta_emails: true,
  });

  // Load from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setForm(prev => ({ ...prev, ...JSON.parse(saved) }));
    } catch (e) { /* ignore */ }
    // Track start (mount = wizard iniciado)
    import('../lib/track').then(m => m.trackEvent('reto21_started', '/mi-reto-21-dias'));
  }, []);

  // Save to localStorage on changes
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(form)); } catch (e) { /* ignore */ }
  }, [form]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const validate = () => {
    setError('');
    if (step === 0) {
      if (!form.nombre.trim() || form.nombre.trim().length < 2) return 'Ingresa tu nombre';
      if (!form.edad || form.edad < 14 || form.edad > 90) return 'Edad inválida (14-90)';
    }
    if (step === 1) {
      if (!form.sustancia) return 'Selecciona la sustancia principal';
      if (form.anos_consumo === '' || form.anos_consumo < 0 || form.anos_consumo > 70) return 'Años de consumo inválidos';
      if (form.edad_inicio !== '' && form.edad_inicio != null) {
        const ei = Number(form.edad_inicio);
        if (ei < 5 || ei > 80) return 'Edad de inicio inválida (5-80)';
      }
    }
    if (step === 5 && (!form.para_que.trim() || form.para_que.trim().length < 20)) {
      return 'Escribe tu propósito (mínimo 20 caracteres)';
    }
    if (step === 7) {
      if (!form.email || !/.+@.+\..+/.test(form.email)) return 'Email inválido — lo necesitamos para enviarte tu PDF';
    }
    return null;
  };

  const next = () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    if (step < 7) setStep(s => s + 1);
    else handleSubmit();
  };

  const prev = () => { setError(''); if (step > 0) setStep(s => s - 1); };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        ...form,
        edad: Number(form.edad),
        anos_consumo: Number(form.anos_consumo),
        edad_inicio: form.edad_inicio !== '' && form.edad_inicio != null ? Number(form.edad_inicio) : null,
      };
      const res = await fetch(`${API_URL}/api/reto21/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'No pudimos generar tu plan');
      setResult(json);
      setStep(8);
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
      import('../lib/track').then(m => m.trackEvent('reto21_completed', '/mi-reto-21-dias'));
    } catch (e) {
      setError(e.message || 'Error al generar el plan');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPDF = () => {
    if (!result?.pdf_base64) return;
    const bin = atob(result.pdf_base64);
    const buf = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
    const blob = new Blob([buf], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = result.pdf_filename || 'Reto-21-Dias.pdf';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    import('../lib/track').then(m => m.trackEvent('reto21_pdf_descargado', '/mi-reto-21-dias'));
  };

  const progress = ((step) / TOTAL_STEPS) * 100;

  return (
    <TooltipProvider>
      
        <title>Mi Reto 21 Días — Plan personalizado | SinAdicciones.org</title>
        <meta name="description" content="Crea tu plan personalizado de 21 días para iniciar la recuperación. Gratis, basado en evidencia (NIDA, SAMHSA, SENDA). Recibe el PDF por email." />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Mi Reto 21 Días — Crea tu plan personalizado gratis" />
        <meta property="og:description" content="8 preguntas, 5 minutos, y recibes tu PDF de 19 páginas + 21 emails de acompañamiento. Gratis." />
        <meta property="og:image" content="https://sinadicciones.org/assets/reto21-landing/hero_summit.jpg" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="720" />
        <meta property="og:url" content="https://sinadicciones.org/mi-reto-21-dias" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://sinadicciones.org/assets/reto21-landing/hero_summit.jpg" />
        <link rel="canonical" href="https://sinadicciones.org/mi-reto-21-dias" />
      

      <main className="min-h-screen bg-slate-950 text-slate-100 pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          {/* Progress bar */}
          {step < 8 && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] uppercase tracking-[0.3em] text-emerald-400 font-mono">Reto 21 Días</p>
                <p className="text-xs text-slate-500 font-mono">Paso {Math.min(step + 1, 8)} de 8</p>
              </div>
              <div className="h-1 bg-slate-800 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-emerald-500"
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.4 }}
                />
              </div>
            </div>
          )}

          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="s0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6" data-testid="reto21-step-0">
                <div>
                  <h1 className="text-3xl sm:text-4xl font-light text-white mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                    Tu Reto 21 Días
                  </h1>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    Empecemos. 5 minutos, 8 preguntas, y recibes un plan personalizado en PDF basado en <i>tu</i> situación. 100% gratis.
                  </p>
                </div>
                <div>
                  <Label className="text-slate-200 mb-1.5 block text-sm">Tu nombre <InfoTip>Lo usaremos para personalizar tu PDF.</InfoTip></Label>
                  <input type="text" value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Ej: María González" className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="reto21-input-nombre" maxLength={60} />
                </div>
                <div>
                  <Label className="text-slate-200 mb-1.5 block text-sm">Tu edad</Label>
                  <input type="number" min={14} max={90} value={form.edad} onChange={e => set('edad', e.target.value)} placeholder="Edad" className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="reto21-input-edad" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-200 mb-1.5 block text-sm">Género <span className="text-slate-500 text-xs">(opcional)</span></Label>
                    <select value={form.genero} onChange={e => set('genero', e.target.value)} className="w-full px-3 py-3 bg-slate-900 border border-slate-700 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="reto21-input-genero">
                      <option value="">—</option>
                      {GENEROS.map(g => (<option key={g.value} value={g.value}>{g.label}</option>))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-200 mb-1.5 block text-sm">Región <span className="text-slate-500 text-xs">(opcional)</span></Label>
                    <select value={form.region} onChange={e => set('region', e.target.value)} className="w-full px-3 py-3 bg-slate-900 border border-slate-700 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="reto21-input-region">
                      <option value="">—</option>
                      {REGIONES_CL.map(r => (<option key={r} value={r}>{r}</option>))}
                    </select>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-6" data-testid="reto21-step-1">
                <div>
                  <h2 className="text-2xl text-white font-light mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Tu sustancia principal</h2>
                  <p className="text-sm text-slate-400">¿Cuál es tu adicción principal o "droga madre"? <InfoTip>La droga madre es tu adicción primaria — la que más impacto tiene en tu vida. Aunque uses otras, hay UNA central.</InfoTip></p>
                </div>
                <select value={form.sustancia} onChange={e => set('sustancia', e.target.value)} className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="reto21-input-sustancia">
                  <option value="">Selecciona</option>
                  {SUSTANCIAS.map(s => (<option key={s} value={s}>{s}</option>))}
                </select>
                <div>
                  <Label className="text-slate-200 mb-1.5 block text-sm">¿Cuántos años llevas consumiendo? <InfoTip>Aproximado. La duración del consumo es relevante para la recuperación.</InfoTip></Label>
                  <input type="number" min={0} max={70} value={form.anos_consumo} onChange={e => set('anos_consumo', e.target.value)} placeholder="Años" className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="reto21-input-anos" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-200 mb-1.5 block text-sm">Edad en que empezaste <span className="text-slate-500 text-xs">(opcional)</span> <InfoTip>La edad de inicio del consumo es un predictor clave del pronóstico.</InfoTip></Label>
                    <input type="number" min={5} max={80} value={form.edad_inicio} onChange={e => set('edad_inicio', e.target.value)} placeholder="Edad de inicio" className="w-full px-3 py-3 bg-slate-900 border border-slate-700 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="reto21-input-edad-inicio" />
                  </div>
                  <div>
                    <Label className="text-slate-200 mb-1.5 block text-sm">¿Habías intentado dejarlo antes? <span className="text-slate-500 text-xs">(opcional)</span></Label>
                    <select value={form.intentos_previos} onChange={e => set('intentos_previos', e.target.value)} className="w-full px-3 py-3 bg-slate-900 border border-slate-700 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="reto21-input-intentos">
                      <option value="">—</option>
                      {INTENTOS_PREVIOS.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
                    </select>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5" data-testid="reto21-step-2">
                <div>
                  <h2 className="text-2xl text-white font-light mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Salud mental</h2>
                  <p className="text-sm text-slate-400">¿Tienes algún diagnóstico de salud mental? <InfoTip>El 40-60% de personas con adicción tienen patología dual. Tratar ambas mejora 3x el pronóstico.</InfoTip> (Puedes seleccionar varias)</p>
                </div>
                <MultiSelect options={SALUD_MENTAL} values={form.salud_mental} onChange={v => set('salud_mental', v)} allowExclusive="Ninguna diagnosticada" />
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5" data-testid="reto21-step-3">
                <div>
                  <h2 className="text-2xl text-white font-light mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Tus gatillos</h2>
                  <p className="text-sm text-slate-400">Situaciones, emociones o contextos que te llevan a consumir. <InfoTip>Conocer los gatillos es la mitad de la batalla. Tu plan tendrá estrategias específicas para cada uno.</InfoTip></p>
                </div>
                <MultiSelect options={GATILLOS} values={form.gatillos} onChange={v => set('gatillos', v)} />
                <div>
                  <Label className="text-slate-200 mb-1.5 block text-sm">¿Algún otro gatillo no listado? (opcional)</Label>
                  <textarea value={form.gatillos_custom} onChange={e => set('gatillos_custom', e.target.value)} placeholder="Describe brevemente..." rows={2} className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none" maxLength={500} data-testid="reto21-input-gatillos-custom" />
                </div>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div key="s4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5" data-testid="reto21-step-4">
                <div>
                  <h2 className="text-2xl text-white font-light mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Tus fortalezas</h2>
                  <p className="text-sm text-slate-400">Personas, actividades y recursos que te sostienen. <InfoTip>Los factores protectores son tu red real de soporte. Reconocerlos te recuerda que NO estás solo.</InfoTip></p>
                </div>
                <MultiSelect options={FORTALEZAS} values={form.fortalezas} onChange={v => set('fortalezas', v)} />
              </motion.div>
            )}

            {step === 5 && (
              <motion.div key="s5" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5" data-testid="reto21-step-5">
                <div>
                  <h2 className="text-2xl text-white font-light mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Tu para qué</h2>
                  <p className="text-sm text-slate-400 leading-relaxed">¿Qué te mueve? ¿Por qué quieres recuperarte? <InfoTip>"Quien tiene un porqué soporta cualquier cómo" — Viktor Frankl. Tu propósito será tu ancla los días difíciles.</InfoTip></p>
                </div>
                <textarea value={form.para_que} onChange={e => set('para_que', e.target.value)} placeholder="Mi familia, mis hijos, mi sueño de... (mínimo 20 caracteres)" rows={6} className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none" maxLength={1000} data-testid="reto21-input-paraque" />
                <p className="text-xs text-slate-500 text-right">{form.para_que.length}/1000</p>
              </motion.div>
            )}

            {step === 6 && (
              <motion.div key="s6" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5" data-testid="reto21-step-6">
                <div>
                  <h2 className="text-2xl text-white font-light mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Hábitos a desarrollar</h2>
                  <p className="text-sm text-slate-400">Selecciona los que más te resuenen. <InfoTip>Atomic Habits: el 1% mejor cada día = 37x al año. Mejor pequeños y constantes que ambiciosos y abandonados.</InfoTip></p>
                </div>
                <MultiSelect options={HABITOS} values={form.habitos} onChange={v => set('habitos', v)} />
              </motion.div>
            )}

            {step === 7 && (
              <motion.div key="s7" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5" data-testid="reto21-step-7">
                <div>
                  <h2 className="text-2xl text-white font-light mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Áreas de vida a mejorar</h2>
                  <p className="text-sm text-slate-400">¿En qué áreas quieres trabajar? <InfoTip>No todo se repara al tiempo. Elegir prioridades te permite avanzar de verdad.</InfoTip></p>
                </div>
                <MultiSelect options={AREAS} values={form.areas} onChange={v => set('areas', v)} />

                {/* Email + drip opt-in (al final para no generar fricción inicial) */}
                <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-xl p-5 mt-6 space-y-4">
                  <div>
                    <p className="text-emerald-300 text-[10px] uppercase tracking-[0.25em] font-mono mb-1">Último paso</p>
                    <h3 className="text-lg text-white font-medium mb-1">¿A dónde te enviamos tu PDF?</h3>
                    <p className="text-xs text-slate-400 leading-relaxed">Tu plan personalizado llegará a tu email en segundos. Sin spam — sólo el contenido del Reto 21.</p>
                  </div>
                  <div>
                    <Label className="text-slate-200 mb-1.5 block text-sm">Tu email</Label>
                    <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="tu@email.com" className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500" data-testid="reto21-input-email" />
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer pt-1">
                    <input type="checkbox" checked={form.acepta_emails} onChange={e => set('acepta_emails', e.target.checked)} className="mt-1 w-4 h-4 accent-emerald-500" data-testid="reto21-input-emails" />
                    <span className="text-sm text-slate-300 leading-relaxed">
                      Quiero recibir un <b className="text-emerald-300">email diario por 21 días</b> con lo que toca para ese día (qué esperar, tu misión y un tip). Puedo darme de baja cuando quiera.
                    </span>
                  </label>
                </div>
              </motion.div>
            )}

            {step === 8 && result && (
              <motion.div key="s8" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12" data-testid="reto21-result">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: 'spring' }} className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                </motion.div>
                <h2 className="text-3xl text-white font-light mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  Tu plan está listo, {form.nombre}
                </h2>
                <p className="text-slate-400 leading-relaxed max-w-md mx-auto mb-8">
                  Hemos creado tu PDF personalizado de 21 días.{result.email_sent ? <> También te lo enviamos a <b className="text-emerald-300">{form.email}</b>.</> : <> <span className="text-amber-400">El email puede tardar unos minutos en llegar.</span></>}
                </p>

                <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
                  <Button onClick={downloadPDF} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-8 py-6" data-testid="reto21-download">
                    <Download className="w-4 h-4 mr-2" /> Descargar mi PDF
                  </Button>
                </div>

                {form.acepta_emails && (
                  <p className="text-xs text-slate-500 mb-8">
                    <Mail className="w-3 h-3 inline mr-1" /> Recibirás 1 email diario por 21 días con el contenido del día.
                  </p>
                )}

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl mx-auto">
                  <Link to="/asesoria" className="px-4 py-3 bg-amber-500 hover:bg-amber-400 text-black rounded-lg text-sm font-bold">Asesoría</Link>
                  <Link to="/cotizar" className="px-4 py-3 bg-slate-900 hover:bg-slate-800 border border-emerald-500/40 text-emerald-300 rounded-lg text-sm">Cotizar</Link>
                  <Link to="/experiencias" className="px-4 py-3 bg-slate-900 hover:bg-slate-800 border border-cyan-500/40 text-cyan-300 rounded-lg text-sm">Experiencias</Link>
                  <Link to="/blog" className="px-4 py-3 bg-slate-900 hover:bg-slate-800 border border-slate-600 text-slate-300 rounded-lg text-sm">Blog</Link>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error */}
          {error && step < 8 && (
            <div className="mt-4 px-4 py-3 bg-red-950/30 border border-red-500/40 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          {/* Nav buttons */}
          {step < 8 && (
            <div className="mt-8 flex items-center justify-between gap-3">
              <Button onClick={prev} variant="ghost" disabled={step === 0 || submitting} className="text-slate-400 hover:text-white" data-testid="reto21-prev">
                <ArrowLeft className="w-4 h-4 mr-2" /> Atrás
              </Button>
              <Button onClick={next} disabled={submitting} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold px-8" data-testid="reto21-next">
                {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generando tu plan...</>) : step === 7 ? (<>Recibir mi PDF gratis <ArrowRight className="w-4 h-4 ml-2" /></>) : (<>Siguiente <ArrowRight className="w-4 h-4 ml-2" /></>)}
              </Button>
            </div>
          )}
        </div>
      </main>
    </TooltipProvider>
  );
}
